import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { BeltRank } from "../../MembersArea";
import { DMC_INSTRUCTION_LINES } from "./daeMahngChunInstructions";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

const demoAliasesByPattern: Record<number, string[]> = {
  1: ["daemahng_chun_1"],
  2: ["daemahng_chun_2"],
  3: ["daemahng_chun_3"],
  4: ["daemahng_chun_4"],
  5: ["daemahng_chun_5"],
  6: ["daemahng_chun_6"],
  7: ["daemahng_chun_7"],
};

export interface DaeMahngChunPattern {
  number: number;
  name: string;
  meaning: string;
}

interface DaeMahngChunProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  patterns: DaeMahngChunPattern[];
  currentPattern: number;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function DaeMahngChun({
  selectedBelt,
  getBeltColor: _getBeltColor,
  patterns,
  currentPattern,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: DaeMahngChunProps) {
  if (patterns.length === 0) return null;

  const fingerprint = patterns.map((p) => p.number).join("|");
  const [activeTab, setActiveTab] = useState(() => String(patterns[0]?.number ?? 1));

  useEffect(() => {
    const nums = patterns.map((p) => p.number);
    const first = nums[0] ?? 1;
    if (presentation === "curriculum") {
      setActiveTab(String(first));
      return;
    }
    const pref =
      currentPattern && nums.includes(currentPattern) ? currentPattern : first;
    setActiveTab(String(pref));
  }, [fingerprint, currentPattern, presentation]);

  const getGradingRequirements = (belt: BeltRank) => {
    switch (belt) {
      case 'WHITE':
        return 'DMC 1';
      case 'YELLOW':
        return 'DMC 1-2 Combo';
      case 'ORANGE':
        return 'DMC 1-2 Combo, DMC 3';
      case 'BLUE':
        return 'DMC 1-2 Combo, 3-4 Combo';
      case 'GREEN':
        return 'DMC 1-5 Combo';
      case 'PURPLE':
        return 'DMC 1-5 Combo, DMC 6';
      case 'BROWN':
      case 'RED':
      case 'NAVY':
        return 'DMC 1-5 Combo, DMC 6-7 Combo';
      case 'BLACK/WHITE':
      case 'BLACK/D1':
      case 'BLACK/D2':
      case 'BLACK/D3':
      case 'BLACK/D4':
      case 'BLACK/D5':
        return 'DMC 1-5, DMC 6-7 Combo';
      default:
        return '';
    }
  };

  const renderInstructions = (pattern: number, instructions: string[]) => {
    // Split instructions into Joonbi and Moves
    const joonbiStep = instructions[0];
    const moveSteps = instructions.slice(1);

    return (
      <div className="space-y-4 pt-3">
        {/* Optional demo video link */}
        {demoAliasesByPattern[pattern] &&
          hasDemo?.(demoAliasesByPattern[pattern]) && (
            <div>
              <button
                type="button"
                className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                onClick={() =>
                  onWatchDemo?.(demoAliasesByPattern[pattern])
                }
              >
                Watch Demonstration Video
              </button>
            </div>
          )}

        {/* Stance Key */}
        <div>
          <h4 className="font-medium text-base mb-2 text-taekwondo-red">KEY</h4>
          <div className="flex flex-row items-start justify-between gap-4">
            <ul className="text-gray-700 space-y-1">
              <li>CS - Cat stance</li>
              <li>BS - Back stance</li>
              <li>FS - Front stance</li>
              <li>TS - Tiger stance</li>
              <li>KH - Knife Hand</li>
            </ul>
            <img 
              src="/images/instructions/dmc_floor_plan.png" 
              alt="DMC Floor Plan" 
              className="h-[100px] w-auto object-contain flex-shrink-0"
              loading="lazy"
            />
          </div>
        </div>

        {/* Instructions */}
        <div className="space-y-4">
          {/* Joonbi */}
          <div>
            <h4 className="font-medium text-base mb-2 text-taekwondo-red">JOONBI</h4>
            <p className="text-gray-700">{joonbiStep}</p>
          </div>

          {/* Moves */}
          <div>
            <h4 className="font-medium text-base mb-2 text-taekwondo-red">MOVES</h4>
            <ol className="list-none space-y-2">
              {moveSteps.map((step, index) => (
                <li key={index} className="flex">
                  <span className="text-taekwondo-red mr-2">{index + 1}.</span>
                  <span className="text-gray-700">{step.includes('KIHAP') ? (
                    <>
                      {step.replace(' KIHAP', '')} <span className="font-semibold">KIHAP</span>
                    </>
                  ) : step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    );
  };

  if (presentation === "curriculum") {
    return (
      <div className="space-y-4 text-left">
        <h2 className="text-xl font-semibold text-taekwondo-red md:text-2xl">
          DaeMahngChun - Big Dream Heaven
        </h2>
        <div>
          <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Required for Grading</h3>
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="text-gray-700">{getGradingRequirements(selectedBelt)}</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
          <ScrollableCurriculumTabStrip key={fingerprint} ariaLabelPrefix="DaeMahngChun patterns">
            {patterns.map((p) => (
              <TabsTrigger
                key={p.number}
                value={String(p.number)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium tabular-nums transition-shadow",
                  beltTabSurfaceClass,
                  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                )}
              >
                {p.number}
              </TabsTrigger>
            ))}
          </ScrollableCurriculumTabStrip>

          {patterns.map((p) => (
            <TabsContent
              key={p.number}
              value={String(p.number)}
              className="mt-0 focus-visible:outline-none"
            >
              <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
                {DMC_INSTRUCTION_LINES[p.number] &&
                  renderInstructions(p.number, DMC_INSTRUCTION_LINES[p.number])}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Grading Requirements Section */}
      <div>
        <h3 className="font-semibold text-lg mb-2 text-taekwondo-red pt-2">Required for Grading</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-gray-700">{getGradingRequirements(selectedBelt)}</p>
        </div>
      </div>

      <Accordion type="single" collapsible className="w-full">
        {patterns.map((pattern) => (
          <AccordionItem 
            key={`dmc-pattern-${pattern.number}`}
            value={`dmc-pattern-${pattern.number}`}
          >
            <AccordionTrigger 
              className={`hover:no-underline ${pattern.number === currentPattern ? 'font-semibold text-taekwondo-blue' : ''}`}
            >
              <div className="flex items-center w-full text-left">
                <span className="flex-1">
                  {pattern.name} ({pattern.meaning})
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {DMC_INSTRUCTION_LINES[pattern.number] &&
                renderInstructions(pattern.number, DMC_INSTRUCTION_LINES[pattern.number])}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
} 